import ExcelJS from "exceljs";
import { get } from "lodash";
import moment from "moment";
import { resolveFileUrl } from "config/env";

/**
 * Печать документа по Excel-шаблону:
 * - %field% заменяется на значение поля документа;
 * - строки с %row.field% дублируются на каждую позицию.
 */

const replacePlaceholders = (str, obj) =>
  str.replace(/%[^%]+%/g, (match) => {
    const key = match.slice(1, -1);
    if (key.startsWith("row")) return `%${key}%`;
    if (key === "time") return moment(obj.time).format("DD.MM.YYYY HH:mm:ss");
    return get(obj, key) ?? "-";
  });

const handleRows = (str, obj) =>
  str.replace(/%[^%]+%/g, (match) => {
    const key = match.slice(1, -1);
    if (!key.startsWith("row")) return `%${key}%`;
    return get(obj, key.slice(4)) ?? "-";
  });

const duplicateTableRows = (worksheet, countToDuplicate) => {
  let rowIndexOffset = 0;
  let isInserted = false;

  worksheet.eachRow((row, rowIndex) => {
    if (isInserted) return;
    const adjustedRowIndex = rowIndex + rowIndexOffset;

    let containsWord = false;
    row.eachCell((cell) => {
      if (cell.value && cell.value.toString().includes("%row")) {
        containsWord = true;
      }
    });

    if (!containsWord) return;

    let newRowIndex = 0;
    for (let i = 0; i < countToDuplicate; i++) {
      newRowIndex = adjustedRowIndex + 1 + i;
      const newRow = worksheet.insertRow(newRowIndex, row.values);
      isInserted = true;

      row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
        const newCell = newRow.getCell(colIndex);
        newCell.style = { ...cell.style };
        if (cell.isHyperlink) {
          newCell.value = { text: cell.text, hyperlink: cell.hyperlink };
        } else if (
          cell.value &&
          typeof cell.value === "object" &&
          cell.value.formula
        ) {
          newCell.value = { ...cell.value };
        }
      });

      if (worksheet.model.merges) {
        worksheet.model.merges.forEach((mergeRange) => {
          try {
            const [startCell, endCell] = mergeRange.split(":");
            const startRow = parseInt(startCell.replace(/\D/g, ""), 10);

            if (startRow === rowIndex) {
              const newStartCell = startCell.replace(startRow, newRowIndex);
              const newEndCell = endCell.replace(startRow, newRowIndex);
              worksheet.mergeCells(`${newStartCell}:${newEndCell}`);
            }
          } catch (e) {
            console.warn(e);
          }
        });
      }
    }

    rowIndexOffset++;
  });
};

export const printDocumentByTemplate = async ({
  fileUrl,
  document: doc,
  docItems,
  fileName,
}) => {
  if (!fileUrl) return;

  const response = await fetch(resolveFileUrl(fileUrl));
  const arrayBuffer = await response.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  workbook.eachSheet((worksheet) => {
    let docItemIndex = 0;
    const maxDocItemIndex = docItems.length - 1;
    duplicateTableRows(worksheet, Math.max(docItems.length - 1, 0));

    worksheet.eachRow((row) => {
      let rowModified = false;
      row.eachCell((cell) => {
        if (typeof cell.value === "string") {
          cell.value = replacePlaceholders(cell.value, doc);
          const updatedValue = handleRows(cell.value, docItems[docItemIndex]);
          if (updatedValue !== cell.value) {
            cell.value = updatedValue;
            rowModified = true;
          }
        }
      });

      if (rowModified && docItemIndex < maxDocItemIndex) {
        docItemIndex++;
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "fp-document.xlsx";
  link.click();
  URL.revokeObjectURL(url);
};
